import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DocumentEmailPreview } from "@autorepair/contracts";
import { ApiError } from "../lib/api";
import { ModernButton, ModernModal, Spinner } from "./ui-modern";

type Phase = "preview" | "sending" | "sent" | "error";

/**
 * "Email … to Customer" as one dialog: shows exactly what will be sent (the
 * server builds the preview with the same code that sends), sends on
 * confirmation, then reports the outcome in the same popup — success (with a
 * Close button) or the error (Try again / Close). Mount it only while open.
 */
export function EmailDocumentDialog({
  kindLabel,
  queryKey,
  loadPreview,
  send,
  onSent,
  onClose,
}: {
  /** "estimate" | "invoice" — used in the title and messages. */
  kindLabel: string;
  queryKey: readonly unknown[];
  loadPreview: () => Promise<DocumentEmailPreview>;
  send: () => Promise<{ sentTo: string }>;
  /** Called after a successful send (e.g. to refresh the "Emailed N×" label). */
  onSent: () => void;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("preview");
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const openedAt = useRef(Date.now());
  useEffect(() => {
    openedAt.current = Date.now();
  }, []);

  const previewQuery = useQuery({ queryKey: [...queryKey, "email-preview"], queryFn: loadPreview, gcTime: 0, retry: false });

  async function doSend(): Promise<void> {
    // Ignore a double-click on the button that opened this dialog landing on Send.
    if (Date.now() - openedAt.current < 600) return;
    setPhase("sending");
    setError(null);
    try {
      const res = await send();
      setSentTo(res.sentTo);
      setPhase("sent");
      onSent();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Could not email the ${kindLabel}. Please try again.`);
      setPhase("error");
    }
  }

  const preview = previewQuery.data;
  const title = phase === "sent" ? "Email Sent" : `Email ${kindLabel[0]?.toUpperCase()}${kindLabel.slice(1)} to Customer`;

  return (
    <ModernModal title={title} wide onCancel={phase === "sending" ? () => undefined : onClose}>
      {phase === "sent" ? (
        <>
          <p className="mb-2 text-[14px] text-[#101b2d]">
            The {kindLabel} was emailed to <strong>{sentTo}</strong>, with the PDF attached.
          </p>
          <p className="mb-6 text-[13px] text-[#6b7a8f]">Replies from the customer will go to your shop's email.</p>
          <div className="flex gap-2.5">
            <ModernButton type="button" onClick={onClose}>
              Close
            </ModernButton>
          </div>
        </>
      ) : previewQuery.isLoading ? (
        <Spinner />
      ) : previewQuery.error || !preview ? (
        <>
          <p className="mb-6 text-[14px] text-[#b3261e]">
            {previewQuery.error instanceof ApiError ? previewQuery.error.message : `Could not prepare the ${kindLabel} email.`}
          </p>
          <ModernButton type="button" variant="secondary" onClick={onClose}>
            Close
          </ModernButton>
        </>
      ) : (
        <>
          {phase === "error" && error && (
            <div className="mb-3 rounded-[10px] border border-[#f0c4c0] bg-[#fdf1f0] px-3.5 py-2.5 text-[13.5px] text-[#b3261e]">{error}</div>
          )}
          <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[13.5px]">
            <dt className="text-[#6b7a8f]">To</dt>
            <dd className="m-0 break-all text-[#101b2d]">{preview.to}</dd>
            <dt className="text-[#6b7a8f]">From</dt>
            <dd className="m-0 break-all text-[#101b2d]">
              {preview.fromName} &lt;{preview.fromAddress}&gt;
            </dd>
            <dt className="text-[#6b7a8f]">Reply-To</dt>
            <dd className="m-0 break-all text-[#101b2d]">{preview.replyTo}</dd>
            <dt className="text-[#6b7a8f]">Subject</dt>
            <dd className="m-0 font-semibold text-[#101b2d]">{preview.subject}</dd>
            <dt className="text-[#6b7a8f]">Attachment</dt>
            <dd className="m-0 break-all text-[#101b2d]">{preview.attachmentFilename}</dd>
          </dl>
          {/* Sandboxed (no scripts, no same-origin): the body is only ever displayed. */}
          <iframe
            title="Email preview"
            sandbox=""
            srcDoc={preview.html}
            className="mb-5 h-[300px] w-full rounded-[10px] border border-[#e2e7ee] bg-[#f4f6f8]"
          />
          <div className="flex gap-2.5">
            <ModernButton type="button" disabled={phase === "sending"} onClick={() => void doSend()}>
              {phase === "sending" ? "Sending…" : phase === "error" ? "Try Again" : "Send Email"}
            </ModernButton>
            <ModernButton type="button" variant="secondary" disabled={phase === "sending"} onClick={onClose}>
              Cancel
            </ModernButton>
          </div>
        </>
      )}
    </ModernModal>
  );
}
